//* react
import React from 'react';

//* material-ui
import { Box, List, Popover } from '@mui/material';

//* project imports
import { useSelector } from 'store';
import { filterUnreadChatUsersByView } from 'utils/chat';
import SingleChatUser from 'views/chats/users/list/SingleChatUser';
import useConfig from 'hooks/useConfig';
import { ThemeDirection } from 'config';

interface UnreadUsersPreviewProps {
  viewId: 'my-chats' | 'not-my-chats' | 'unassigned-chats' | 'bots-chats' | 'chats' | undefined;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const UnreadUsersPreview: React.FC<UnreadUsersPreviewProps> = ({ viewId, open, anchorEl, onClose }) => {
  const unreadChatUsers = useSelector((state) => state.unreadChatUsers);
  const userId = useSelector((state) => state.user?.Udid);
  const { themeDirection } = useConfig();

  const filteredUsers = React.useMemo(() => {
    return filterUnreadChatUsersByView(unreadChatUsers, userId, viewId);
  }, [unreadChatUsers, userId, viewId]);

  const parseViewId = (
    viewId: 'my-chats' | 'not-my-chats' | 'unassigned-chats' | 'bots-chats' | 'chats' | undefined,
  ) => {
    switch (viewId) {
      case 'my-chats':
        return 'my';
      case 'not-my-chats':
        return 'notMy';
      case 'unassigned-chats':
        return 'unassigned';
      case 'bots-chats':
        return 'bots';
      case 'chats':
        return 'all';
      default:
        return undefined;
    }
  };

  return (
    <Popover
      open={open && filteredUsers.length > 0}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: themeDirection === ThemeDirection.RTL ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: themeDirection === ThemeDirection.RTL ? 'right' : 'left',
      }}
      onClose={onClose}
      disableRestoreFocus
      disableScrollLock={false}
      // disablePortal
      hideBackdrop
      sx={{
        pointerEvents: 'none',
      }}
      slotProps={{
        paper: {
          sx: {
            scale: '0.85',
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <Box px={1.5}>
        <List
          sx={{
            maxHeight: 500,
            maxWidth: 450,
            overflow: 'auto',
          }}
        >
          {filteredUsers.map((user) => (
            <SingleChatUser user={user} interactive={false} key={user.Id} viewId={parseViewId(viewId)} />
          ))}
        </List>
      </Box>
    </Popover>
  );
};

export default UnreadUsersPreview;
